import { handleResponse } from './errorHandler';
import AccessTokenManager from './accessTokenManager';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

export interface SOWConversationResponse {
  conversation: any;
  conversationResponse: {
    outstandingQuestion?: {
      QuestionText: string;
      Options: string[];
      NeedPicture: boolean;
    };
    workItemsForConfirmation?: {
      category: string;
      description: string;
      reasoning: string;
    }[];
    confirmationQuestion?: string;
    Answer?: any;
    // Work item refinement specific fields
    refinementComplete?: boolean;
    refinedDetails?: {
      scope: string;
      specifications: string[];
      estimationReady: boolean;
    };
    summary?: string;
    HaveAnswer?: boolean;
    // Batch processing fields
    questionBatch?: {
      questions: any[];
    };
    batchProgress?: {
      current: number;
      total: number;
    };
  };
}

export interface QuestionnaireQuestion {
  id: string;
  text: string;
  type: 'checkbox' | 'yes_no' | 'single_choice' | 'text';
  options?: string[] | null;
  placeholder?: string | null;
}

export interface QuestionnaireImage {
  id: string;
  title: string;
  helpText: string;
  targetDatapoints: string[];
  component?: string; // Component this image is for (e.g., "kitchen", "master_bathroom")
  expectedImageType?: string; // What type of image is expected (e.g., "kitchen_interior")
}

export interface QuestionnaireSection {
  id: string;
  title: string;
  type: 'global' | 'job';
  jobType?: string; // e.g., "kitchen", "bathroom" - only for job type
  questions: QuestionnaireQuestion[];
}

export interface QuestionnaireResponse {
  sowId: string;
  sections: QuestionnaireSection[];
  projectType: string;
  address: string;
}

export interface QuestionnaireAnswer {
  questionId: string;
  answer: string | boolean; // Can be text, "Yes", "No", or boolean for checkboxes
  section?: string; // Section ID this answer belongs to
}

export interface QuestionnaireSubmissionResponse {
  status: string;
  workItems: any[];
  quickEstimate: {
    totalCostLow: number;
    totalCostHigh: number;
    totalLaborLow: number;
    totalLaborHigh: number;
    totalMaterialLow: number;
    totalMaterialHigh: number;
    breakdown: any[];
  };
  summary: string;
}

export const sowConversationService = {
  // NEW PHOTO-FIRST FLOW: Get image requests
  async getImageRequests(sowId: string, requireAuth: boolean = true): Promise<{
    sowId: string;
    images: QuestionnaireImage[];
    projectType: string;
    address: string;
  }> {
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      if (requireAuth) {
        const token = AccessTokenManager.getAccessToken();
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      }

      const response = await fetch(`${API_BASE_URL}/statementofwork/${sowId}/initial-intake/images`, {
        method: 'GET',
        headers,
      });

      return await handleResponse(response);
    } catch (error) {
      console.error('Get image requests error:', error);
      throw error;
    }
  },

  // NEW PHOTO-FIRST FLOW: Generate questions from uploaded images
  async getQuestionsFromImages(sowId: string, imageIds: string[], requireAuth: boolean = true): Promise<QuestionnaireResponse> {
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      if (requireAuth) {
        const token = AccessTokenManager.getAccessToken();
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      }

      const response = await fetch(`${API_BASE_URL}/statementofwork/${sowId}/initial-intake/questions`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ imageIds }),
      });

      return await handleResponse(response);
    } catch (error) {
      console.error('Get questions from images error:', error);
      throw error;
    }
  },

  // Start a new SOW conversation
  async startConversation(sowId: string, initialDescription?: string, imageAnalysis?: any): Promise<SOWConversationResponse> {
    try {
      const token = AccessTokenManager.getAccessToken();
      
      if (!token) {
        throw new Error('No auth token available');
      }
      
      const response = await fetch(`${API_BASE_URL}/statementofwork/${sowId}/conversation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          initialDescription,
          imageAnalysis
        }),
      });

      return await handleResponse(response);
    } catch (error) {
      console.error('SOW conversation service error:', error);
      throw error;
    }
  },

  // Continue existing SOW conversation
  async continueConversation(conversationId: string, conversationData: any): Promise<SOWConversationResponse> {
    try {
      const token = AccessTokenManager.getAccessToken();
      
      if (!token) {
        throw new Error('No auth token available');
      }
      
      const response = await fetch(`${API_BASE_URL}/statementofwork/conversation/${conversationId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(conversationData),
      });

      return await handleResponse(response);
    } catch (error) {
      console.error('SOW conversation service error:', error);
      throw error;
    }
  },

  // Get existing SOW conversation
  async getConversation(conversationId: string): Promise<any> {
    try {
      const token = AccessTokenManager.getAccessToken();
      
      if (!token) {
        throw new Error('No auth token available');
      }
      
      const response = await fetch(`${API_BASE_URL}/statementofwork/conversation/${conversationId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      return await handleResponse(response);
    } catch (error) {
      console.error('SOW conversation service error:', error);
      throw error;
    }
  },

  // Get structured questionnaire for SOW (Smart Initial Intake)
  async getQuestionnaire(sowId: string, requireAuth: boolean = true): Promise<QuestionnaireResponse> {
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      if (requireAuth) {
        const token = AccessTokenManager.getAccessToken();
        if (!token) {
          throw new Error('No auth token available');
        }
        headers['Authorization'] = `Bearer ${token}`;
      }

      // Use new endpoint that returns static checklists based on jobs
      const response = await fetch(`${API_BASE_URL}/statementofwork/${sowId}/initial-intake/questions`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ imageIds: [] }) // No imageIds needed for static checklists
      });

      return await handleResponse(response);
    } catch (error) {
      console.error('SOW initial intake service error:', error);
      throw error;
    }
  },

  // Save questionnaire progress (individual answers)
  async saveQuestionnaireProgress(sowId: string, answers: QuestionnaireAnswer[], requireAuth: boolean = true): Promise<{ status: string; totalAnswered: number }> {
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      if (requireAuth) {
        const token = AccessTokenManager.getAccessToken();
        if (!token) {
          throw new Error('No auth token available');
        }
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_BASE_URL}/statementofwork/${sowId}/questionnaire/progress`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          answers
        }),
      });

      return await handleResponse(response);
    } catch (error) {
      console.error('SOW questionnaire progress service error:', error);
      throw error;
    }
  },

  // Submit completed questionnaire (Smart Initial Intake)
  async submitQuestionnaire(sowId: string, answers: QuestionnaireAnswer[], requireAuth: boolean = true): Promise<QuestionnaireSubmissionResponse> {
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      if (requireAuth) {
        const token = AccessTokenManager.getAccessToken();
        if (!token) {
          throw new Error('No auth token available');
        }
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_BASE_URL}/statementofwork/${sowId}/initial-intake/submit`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          answers,
          imageIds: []  // Image IDs collected during questionnaire (empty for now)
        }),
      });

      return await handleResponse(response);
    } catch (error) {
      console.error('SOW initial intake submission service error:', error);
      throw error;
    }
  },

  // Start work item refinement
  async startWorkItemRefinement(sowId: string, workItemId: string): Promise<SOWConversationResponse> {
    try {
      const token = AccessTokenManager.getAccessToken();
      
      if (!token) {
        throw new Error('No auth token available');
      }
      
      const response = await fetch(`${API_BASE_URL}/statementofwork/${sowId}/workitem/${workItemId}/refinement`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({}),
      });

      return await handleResponse(response);
    } catch (error) {
      console.error('Work item refinement start service error:', error);
      throw error;
    }
  },

  // Continue work item refinement conversation
  async continueWorkItemRefinement(conversationId: string, conversationData: any): Promise<SOWConversationResponse> {
    try {
      const token = AccessTokenManager.getAccessToken();
      
      if (!token) {
        throw new Error('No auth token available');
      }
      
      const response = await fetch(`${API_BASE_URL}/statementofwork/workitem/refinement/${conversationId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(conversationData),
      });

      return await handleResponse(response);
    } catch (error) {
      console.error('Work item refinement continue service error:', error);
      throw error;
    }
  },

  // Associate image with work item
  async associateWorkItemImage(sowId: string, workItemId: string, imageId: string, analysisContext: string): Promise<any> {
    try {
      const token = AccessTokenManager.getAccessToken();
      
      if (!token) {
        throw new Error('No auth token available');
      }
      
      const response = await fetch(`${API_BASE_URL}/statementofwork/${sowId}/workitem/${workItemId}/image/${imageId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          analysisContext
        }),
      });

      return await handleResponse(response);
    } catch (error) {
      console.error('Work item image association service error:', error);
      throw error;
    }
  },

  // NEW ARCHITECTURE: Questionnaire methods
  async getQuestionnaires(sowId: string): Promise<{ questionnaires: any[]; workItems: any[] }> {
    try {
      const token = AccessTokenManager.getAccessToken();
      
      if (!token) {
        throw new Error('No auth token available');
      }
      
      const response = await fetch(`${API_BASE_URL}/statementofwork/${sowId}/questionnaires`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      return await handleResponse(response);
    } catch (error) {
      console.error('Get questionnaires service error:', error);
      throw error;
    }
  },

  async startQuestionnaire(sowId: string, questionnaireId: string): Promise<any> {
    try {
      const token = AccessTokenManager.getAccessToken();
      
      if (!token) {
        throw new Error('No auth token available');
      }
      
      const response = await fetch(`${API_BASE_URL}/statementofwork/${sowId}/questionnaire/${questionnaireId}/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({}),
      });

      return await handleResponse(response);
    } catch (error) {
      console.error('Start questionnaire service error:', error);
      throw error;
    }
  },

  async completeQuestionnaire(sowId: string, questionnaireId: string, answers: any[]): Promise<any> {
    try {
      const token = AccessTokenManager.getAccessToken();
      
      if (!token) {
        throw new Error('No auth token available');
      }
      
      const response = await fetch(`${API_BASE_URL}/statementofwork/${sowId}/questionnaire/${questionnaireId}/complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          answers
        }),
      });

      return await handleResponse(response);
    } catch (error) {
      console.error('Complete questionnaire service error:', error);
      throw error;
    }
  }
};